import { create } from 'zustand';

export type ToastKind = 'success' | 'error' | 'info' | 'loading';

export interface ToastItem {
    id: string;
    kind: ToastKind;
    message: string;
    duration?: number;
}

interface ToastState {
    items: ToastItem[];
    push: (item: Omit<ToastItem, 'id'>) => string;
    dismiss: (id: string) => void;
    clear: () => void;
}

const useToastStore = create<ToastState>((set, get) => ({
    items: [],
    push: item => {
        const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        set(s => ({ items: [...s.items, { ...item, id }] }));
        if (item.kind !== 'loading') {
            const duration = item.duration ?? 4000;
            setTimeout(() => get().dismiss(id), duration);
        }
        return id;
    },
    dismiss: id => set(s => ({ items: s.items.filter(i => i.id !== id) })),
    clear: () => set({ items: [] })
}));

export function useToasts() {
    return useToastStore(s => s.items);
}

export function useToastDismiss() {
    return useToastStore(s => s.dismiss);
}

export const toast = {
    success: (message: string, duration?: number) =>
        useToastStore.getState().push({ kind: 'success', message, duration }),
    error: (message: string, duration?: number) =>
        useToastStore.getState().push({ kind: 'error', message, duration: duration ?? 5000 }),
    info: (message: string, duration?: number) => useToastStore.getState().push({ kind: 'info', message, duration }),
    loading: (message: string) => useToastStore.getState().push({ kind: 'loading', message }),
    dismiss: (id: string) => useToastStore.getState().dismiss(id),
    clear: () => useToastStore.getState().clear()
};
